import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Boxes, Layers } from 'lucide-react';
import caseStudies from '../data/caseStudies.json';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import ServicesSection from '../components/ServicesSection';
import AboutSection from '../components/AboutSection';
import FeaturesSection from '../components/FeaturesSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { organizationSchema } from '../utils/structuredData';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="3D Capture for Construction & Commercial Real Estate | DSG London"
        description="Matterport progress capture, point cloud surveys and 3D tours for construction and commercial real estate. From £495, delivered in 48 hours. London and UK."
        keywords="matterport uk, 3d scanning uk, matterport london, matterport manchester, 3d scanning nationwide, point cloud survey uk, matterport virtual tour, laser scanning uk, digital twin uk, scan to bim, 3d laser scanning uk, matterport service provider, virtual tour uk, reality capture, building scanning, point cloud survey, revit bim modelling, drone surveys, construction scanning, NHS virtual tours, as-built surveys, heritage scanning, commercial property scanning"
        canonicalUrl="https://www.digitalscanninggroup.com/"
        structuredData={organizationSchema}
      />
      <Navbar scrolled={scrolled} />
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <FeaturesSection />
      {/* Case Studies Section — driven by src/data/caseStudies.json */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Case Studies</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Recent projects delivered for contractors and building owners. Client names are withheld at their request; the scope and outcomes are as delivered.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((cs, i) => {
              const Icon = [Building2, Boxes, Layers][i % 3];
              return (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 flex flex-col"
                >
                  <div className="inline-flex p-3 bg-blue-500/10 rounded-xl mb-5 self-start">
                    <Icon className="h-7 w-7 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{cs.shortTitle}</h3>
                  <p className="text-sm text-blue-400 font-medium mb-4">{cs.client} · {cs.location}</p>
                  <p className="text-gray-400 text-sm leading-relaxed flex-1">{cs.excerpt}</p>
                  <div className="pt-4 mt-4 border-t border-gray-700 grid grid-cols-3 gap-2">
                    {cs.stats.map((st) => (
                      <div key={st.label}>
                        <div className="text-lg font-bold text-white">{st.value}</div>
                        <div className="text-[11px] text-gray-500 leading-tight">{st.label}</div>
                      </div>
                    ))}
                  </div>
                  <span className="mt-5 text-sm font-semibold text-cyan-400">Read the case study →</span>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/case-studies" className="inline-block px-6 py-3 border border-gray-600 text-gray-200 rounded-xl hover:border-cyan-400 hover:text-cyan-400 transition-colors">
              All case studies
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default HomePage;
